import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../utils/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async (forceLogoutOn401 = false) => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      localStorage.setItem("letzservices_user", JSON.stringify(response.data));
    } catch (error) {
      // Try to restore from localStorage if API fails temporarily
      const savedUser = localStorage.getItem("letzservices_user");
      if (savedUser && !forceLogoutOn401) {
        // Keep the saved user - don't logout on transient 401 errors
        try {
          setUser(JSON.parse(savedUser));
        } catch (_) {
          setUser(null);
        }
      } else if (error.response?.status === 401 && forceLogoutOn401) {
        // Only logout if explicitly forced (e.g., on initial load)
        setUser(null);
        localStorage.removeItem("letzservices_user");
      } else if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (_) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []); // API/axios are module-level constants; setUser/setLoading are stable state setters

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes("session_id=") || window.location.search?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // On initial load, force logout on 401 (no valid session)
    checkAuth(true);
  }, [checkAuth]);

  // Re-check auth periodically to keep session alive
  useEffect(() => {
    const interval = setInterval(() => {
      if (user) {
        checkAuth();
      }
    }, 5 * 60 * 1000); // Every 5 minutes
    return () => clearInterval(interval);
  }, [user, checkAuth]);

  const login = () => {
    window.location.href = "/auth";
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      localStorage.removeItem("letzservices_user");
      localStorage.removeItem("letzservices_session");
      window.location.href = "/";
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
